import * as FeatureInfoActions from './feature-info.actions';
import { Action, createReducer, on } from '@ngrx/store';
import { FeatureInfoState, initialFeatureInfoState } from './feature-info.state';
import { LoadingStateEnum } from '@tailormap-viewer/shared';
import { FeatureInfoLayerModel } from '../models/feature-info-layer.model';

const onLoadFeatureInfo = (
  state: FeatureInfoState,
  payload: ReturnType<typeof FeatureInfoActions.loadFeatureInfo>,
): FeatureInfoState => ({
  ...state,
  mapCoordinates: payload.mapCoordinates,
  mouseCoordinates: payload.mouseCoordinates,
  dialogVisible: payload.layers.length > 0,
  features: [],
  columnMetadata: [],
  attachmentMetadata: [],
  layers: payload.layers,
  selectedLayerId: payload.layers.length === 1 ? payload.layers[0].id : state.selectedLayerId,
  showingBookmarkFeatures: false,
});

const onFeatureInfoLoaded = (
  state: FeatureInfoState,
  payload: ReturnType<typeof FeatureInfoActions.featureInfoLoaded>,
): FeatureInfoState => {
  const layerIdx = state.layers.findIndex(l => l.id === payload.featureInfo.layerId);
  if (layerIdx === -1) {
    return state;
  }
  const updatedLayers: FeatureInfoLayerModel[] = [
    ...state.layers.slice(0, layerIdx),
    {
      ...state.layers[layerIdx],
      loading: payload.featureInfo.error ? LoadingStateEnum.FAILED : LoadingStateEnum.LOADED,
      totalCount: payload.featureInfo.features.length,
      error: payload.featureInfo.error,
      template: payload.featureInfo.template,
      selectedFeatureId: payload.featureInfo.features.length > 0
        ? payload.featureInfo.features[0].__fid
        : undefined,
    },
    ...state.layers.slice(layerIdx + 1),
  ];
  const allLoaded = updatedLayers.every(l => l.loading !== LoadingStateEnum.LOADING);
  let selectedLayerId = state.selectedLayerId;
  if (allLoaded
    && updatedLayers.length > 0
    // if we don't have a layer selected already or the selected layer does not exist anymore, select the first with items
    && (!selectedLayerId || !updatedLayers.some(l => l.id === selectedLayerId))) {
    selectedLayerId = updatedLayers.find(l => (l.totalCount || 0) > 0)?.id;
  }
  return {
    ...state,
    features: [
      ...state.features,
      ...payload.featureInfo.features,
    ],
    columnMetadata: [
      ...state.columnMetadata,
      ...payload.featureInfo.columnMetadata,
    ],
    attachmentMetadata: [
      ...state.attachmentMetadata,
      ...payload.featureInfo.attachmentMetadata,
    ],
    layers: updatedLayers,
    selectedLayerId,
  };
};

const onHideFeatureInfoDialog = (state: FeatureInfoState): FeatureInfoState => ({
  ...state,
  dialogVisible: false,
  dialogCollapsed: false,
  showingBookmarkFeatures: false,
});

const onExpandCollapseFeatureInfoDialog = (state: FeatureInfoState): FeatureInfoState => ({
  ...state,
  dialogCollapsed: !state.dialogCollapsed,
});

const onExpandCollapseFeatureInfoLayerList = (state: FeatureInfoState): FeatureInfoState => ({
  ...state,
  layerListCollapsed: !state.layerListCollapsed,
});

const onSetSelectedFeatureInfoLayer = (
  state: FeatureInfoState,
  payload: ReturnType<typeof FeatureInfoActions.setSelectedFeatureInfoLayer>,
): FeatureInfoState => ({
  ...state,
  selectedLayerId: payload.layer,
});

const selectNextFeature = (state: FeatureInfoState, direction: 'next' | 'previous'): FeatureInfoState => {
  if (!state.selectedLayerId) {
    return state;
  }
  const selectedLayerIdx = state.layers.findIndex(l => l.id === state.selectedLayerId);
  if (selectedLayerIdx === -1) {
    return state;
  }
  const selectedLayer = state.layers[selectedLayerIdx];
  const features = state.features.filter(f => f.layerId === state.selectedLayerId);
  const idx = features.findIndex(f => f.__fid === selectedLayer?.selectedFeatureId);
  let nextIdx = 0;
  if (direction === 'next') {
    nextIdx = idx === -1 || features.length === idx + 1 ? 0 : idx + 1;
  }
  if (direction === 'previous') {
    nextIdx = idx > 0 ? idx - 1 : features.length - 1;
  }
  return {
    ...state,
    layers: [
      ...state.layers.slice(0, selectedLayerIdx),
      { ...selectedLayer, selectedFeatureId: features[nextIdx].__fid },
      ...state.layers.slice(selectedLayerIdx + 1),
    ],
  };
};

const onShowNextFeatureInfoFeature = (state: FeatureInfoState): FeatureInfoState => selectNextFeature(state, 'next');

const onShowPreviousFeatureInfoFeature = (state: FeatureInfoState): FeatureInfoState => selectNextFeature(state, 'previous');

const onReopenFeatureInfoDialog = (state: FeatureInfoState): FeatureInfoState => ({
  ...state,
  dialogVisible: true,
});

const onUpdateFeatureInFeatureInfo = (
  state: FeatureInfoState,
  payload: ReturnType<typeof FeatureInfoActions.updateFeatureInFeatureInfo>,
): FeatureInfoState => {
  const featureIdx = state.features.findIndex(f => f.__fid === payload.feature.__fid);
  if (featureIdx === -1) {
    return state;
  }
  return {
    ...state,
    features: [
      ...state.features.slice(0, featureIdx),
      payload.feature,
      ...state.features.slice(featureIdx + 1),
    ],
  };
};

const onSetFeatureInfoLayers = (
  state: FeatureInfoState,
  payload: ReturnType<typeof FeatureInfoActions.setFeatureInfoLayers>,
): FeatureInfoState => {
  return {
    ...state,
    layers: payload.layers,
  };
};

const onFeatureInfoOpenWithBookmarkFeatures = (state: FeatureInfoState): FeatureInfoState => ({
  ...state,
  dialogVisible: true,
  showingBookmarkFeatures: true,
});

const onEmptyFeatureInfo = (state: FeatureInfoState): FeatureInfoState => ({
  ...state,
  features: [],
  columnMetadata: [],
  attachmentMetadata: [],
  layers: [],
  dialogVisible: false,
  dialogCollapsed: false,
  showingBookmarkFeatures: false,
});

const featureInfoReducerImpl = createReducer<FeatureInfoState>(
  initialFeatureInfoState,
  on(FeatureInfoActions.loadFeatureInfo, onLoadFeatureInfo),
  on(FeatureInfoActions.featureInfoLoaded, onFeatureInfoLoaded),
  on(FeatureInfoActions.hideFeatureInfoDialog, onHideFeatureInfoDialog),
  on(FeatureInfoActions.expandCollapseFeatureInfoDialog, onExpandCollapseFeatureInfoDialog),
  on(FeatureInfoActions.expandCollapseFeatureInfoLayerList, onExpandCollapseFeatureInfoLayerList),
  on(FeatureInfoActions.setSelectedFeatureInfoLayer, onSetSelectedFeatureInfoLayer),
  on(FeatureInfoActions.showNextFeatureInfoFeature, onShowNextFeatureInfoFeature),
  on(FeatureInfoActions.showPreviousFeatureInfoFeature, onShowPreviousFeatureInfoFeature),
  on(FeatureInfoActions.reopenFeatureInfoDialog, onReopenFeatureInfoDialog),
  on(FeatureInfoActions.updateFeatureInFeatureInfo, onUpdateFeatureInFeatureInfo),
  on(FeatureInfoActions.setFeatureInfoLayers, onSetFeatureInfoLayers),
  on(FeatureInfoActions.openFeatureInfoWithBookmarkFeatures, onFeatureInfoOpenWithBookmarkFeatures),
  on(FeatureInfoActions.emptyFeatureInfo, onEmptyFeatureInfo),
);
export const featureInfoReducer = (state: FeatureInfoState | undefined, action: Action) => featureInfoReducerImpl(state, action);
